import React, { useEffect, useState } from "react";
import { SimpleTable } from "../../ui/SimpleTable/SimpleTable";
import { ConfirmDialog } from "../../ConfirmDialog/ConfirmDialog";
import { useFetchPagedGroupGeofence } from "../../../../../hooks/guide/useFetchPagedGroupGeofence";
import { GroupGeofence } from "../../../../../types/api/guide/groupGeofence.types";
import { groupGeofenceService } from "../../../../../services/guide/groupGeofence-service";
import { GroupGeofenceAddModal } from "../../ui/EntityModal/GroupGeofenceAddModal";
import useAppSnackbar from "../../../../../hooks/useAppSnackbar";
import { useDebouncedValue } from "../../../../../hooks/useDebouncedValue";
import { PagedResultDto } from "../../../../../types/api/PagedResultDto";

interface GroupGeofenceTableProps {
    title: string;
}

export const GroupGeofenceTable = ({ title }: GroupGeofenceTableProps) => {
    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 350);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const { data: paged, isLoading, error, refetch } = useFetchPagedGroupGeofence(
        page,
        pageSize,
        debouncedSearch.trim() || undefined
    );

    const [toDelete, setToDelete] = useState<GroupGeofence | null>(null);
    const [openModal, setOpenModal] = useState<{ isOpen: boolean; id: string }>({
        isOpen: false,
        id: "",
    });

    const pageData = paged as PagedResultDto<GroupGeofence> | null | undefined;
    const items = pageData?.items ?? [];
    const totalCount = pageData?.totalCount ?? 0;

    const handleDelete = async (id: string) => {
        try {
            await groupGeofenceService.delete(id);
            showSuccess("Успешное удаление");
            refetch();
        } catch (e: any) {
            showError(e.response?.data);
        }
    };

    return (
        <>
            <SimpleTable<GroupGeofence>
                title={title}
                columns={[{ key: "name", title: "Название" }]}
                loading={isLoading}
                error={error}
                data={items}
                enableSearchByName
                searchPlaceholder="Поиск по названию"
                serverPagination={{
                    totalCount,
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onPageSizeChange: (s) => {
                        setPageSize(s);
                        setPage(1);
                    },
                    searchValue: search,
                    onSearchChange: setSearch,
                }}
                getRowKey={(r) => r.id}
                getRowActions={(row) => ({
                    onEdit: () => setOpenModal({ isOpen: true, id: row.id }),
                    onDelete: () => setToDelete(row),
                })}
                emptyText="Справочник пуст"
                setOpen={() => setOpenModal({ isOpen: true, id: "" })}
            />

            <ConfirmDialog
                open={!!toDelete}
                title="Удалить группу геозон?"
                description={toDelete ? `Будет удалена: ${toDelete.name}` : undefined}
                onCancel={() => setToDelete(null)}
                onConfirm={() => {
                    if (!toDelete) return;
                    handleDelete(toDelete.id);
                    setToDelete(null);
                }}
            />

            {openModal.isOpen && (
                <GroupGeofenceAddModal
                    id={openModal.id}
                    onClose={() => setOpenModal({ isOpen: false, id: "" })}
                    refetchData={refetch}
                />
            )}
            <SnackbarComponent />
        </>
    );
};
